import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart
} from 'recharts';
// Generic interface for chart data points
interface BaseChartDataPoint {
  week: string;
  [key: string]: any;
}

interface PPCLineChartProps<T extends BaseChartDataPoint = BaseChartDataPoint> {
  data: T[];
  title: string;
  leftMetric: {
    key: string;
    name: string;
    color: string;
    gradientId: string;
    type: 'dollar' | 'dollarCents' | 'percentage';
  };
  rightMetric: {
    key: string;
    name: string;
    color: string;
    gradientId: string;
    type: 'dollar' | 'dollarCents' | 'percentage';
  };
  height?: number;
  useSingleYAxis?: boolean;
}

// Format values based on metric type
const formatValue = (value: number, metricType: 'dollar' | 'dollarCents' | 'percentage') => {
  switch (metricType) {
    case 'dollar':
      return `$${Math.round(value).toLocaleString()}`;
    case 'dollarCents':
      return `$${value.toFixed(2)}`;
    case 'percentage':
      return `${Math.round(value * 100)}%`;
    default:
      return value.toString();
  }
};

// Format Y-axis tick values
const formatYAxisTick = (value: number, metricType: 'dollar' | 'dollarCents' | 'percentage') => {
  switch (metricType) {
    case 'dollar':
      return `$${Math.round(value).toLocaleString()}`;
    case 'dollarCents':
      return `$${value.toFixed(2)}`;
    case 'percentage':
      return `${Math.round(value * 100)}%`;
    default:
      return value.toString();
  }
};

const PPCLineChart = <T extends BaseChartDataPoint>({
  data,
  title,
  leftMetric,
  rightMetric,
  height = 300,
  useSingleYAxis = false
}: PPCLineChartProps<T>) => {
  // Check if we have any meaningful data
  const hasData = data && data.length > 0 && data.some(point =>
    (point[leftMetric.key] as number) > 0 || (point[rightMetric.key] as number) > 0
  );

  // Calculate dynamic Y-axis domains with appropriate rounding
  const calculateDomain = (values: number[], metricType: 'dollar' | 'dollarCents' | 'percentage') => {
    const validValues = values.filter(v => v > 0);
    if (validValues.length === 0) return [0, 1];
    
    const minValue = Math.min(...validValues);
    const maxValue = Math.max(...validValues);
    const range = maxValue - minValue;
    
    // Add 10% padding to top and bottom for better visualization
    const padding = range > 0 ? range * 0.1 : maxValue * 0.1;
    
    let domainMin = Math.max(0, minValue - padding);
    let domainMax = maxValue + padding;
    
    // Round to appropriate increments based on metric type
    if (metricType === 'dollar') {
      // Round to nearest $100
      domainMin = Math.floor(domainMin / 100) * 100;
      domainMax = Math.ceil(domainMax / 100) * 100;
    } else if (metricType === 'dollarCents') {
      // Round to nearest $0.10
      domainMin = Math.floor(domainMin * 10) / 10;
      domainMax = Math.ceil(domainMax * 10) / 10;
    } else if (metricType === 'percentage') {
      // Round to nearest 1% (0.01 in decimal)
      domainMin = Math.floor(domainMin * 100) / 100;
      domainMax = Math.ceil(domainMax * 100) / 100;
    }
    
    return [domainMin, domainMax];
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Filter out Area components by checking if dataKey contains 'Area'
      const linePayload = payload.filter((entry: any) =>
        !entry.dataKey.includes('Area') && entry.name && entry.name.trim() !== ''
      );
      
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{label}</p>
          {linePayload.map((entry: any, index: number) => {
            const metricType = entry.dataKey === leftMetric.key ? leftMetric.type : rightMetric.type;
            return (
              <p key={index} className="text-sm" style={{ color: entry.color }}>
                <span className="font-medium">{entry.name}:</span> {formatValue(entry.value, metricType)}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Custom legend content that only shows Line components
  const CustomLegend = (props: any) => {
    const { payload } = props;
    // Filter to only show Line components (not Area components)
    const linePayload = payload?.filter((entry: any) =>
      !entry.dataKey.includes('Area') && entry.value && entry.value.trim() !== ''
    ) || [];

    return (
      <div className="flex justify-center items-center gap-6 pt-5">
        {linePayload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-4 h-0.5"
              style={{ backgroundColor: entry.color }}
            />
            <span
              className="text-sm font-medium"
              style={{ color: entry.color }}
            >
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Create enhanced data with shadow keys for Area components
  const enhancedData = data?.map(point => ({
    ...point,
    [`${leftMetric.key}Area`]: point[leftMetric.key],
    [`${rightMetric.key}Area`]: point[rightMetric.key]
  })) || [];

  // Calculate domains for both metrics
  const leftValues = data?.map(point => (point[leftMetric.key] as number) || 0) || [];
  const rightValues = data?.map(point => (point[rightMetric.key] as number) || 0) || [];
  
  let leftDomain, rightDomain;
  
  if (useSingleYAxis) {
    // When using single Y-axis, combine all values and use the same domain for both
    const allValues = [...leftValues, ...rightValues];
    const combinedDomain = calculateDomain(allValues, leftMetric.type); // Use leftMetric type for formatting
    leftDomain = combinedDomain;
    rightDomain = combinedDomain;
  } else {
    // Use separate domains for each metric
    leftDomain = calculateDomain(leftValues, leftMetric.type);
    rightDomain = calculateDomain(rightValues, rightMetric.type);
  }

  if (!hasData) {
    return (
      <div 
        className="flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600"
        style={{ height }}
      >
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">{title}</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Chart Title */}
      <div className="mb-4 text-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>

      {/* Chart Container */}
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={enhancedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            {/* Gradient Definitions */}
            <defs>
              <linearGradient id={leftMetric.gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={leftMetric.color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={leftMetric.color} stopOpacity={0} />
              </linearGradient>
              <linearGradient id={rightMetric.gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={rightMetric.color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={rightMetric.color} stopOpacity={0} />
              </linearGradient>
            </defs>

            {/* Grid */}
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#e5e7eb" 
              className="dark:stroke-gray-600"
            />

            {/* X Axis */}
            <XAxis
              dataKey="week"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              className="dark:fill-gray-400"
            />

            {/* Y Axis Configuration */}
            {useSingleYAxis ? (
              // Single Y-axis for both metrics
              <YAxis
                yAxisId="left"
                domain={leftDomain}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickFormatter={(value) => formatYAxisTick(value, leftMetric.type)}
              />
            ) : (
              <>
                {/* Left Y Axis */}
                <YAxis
                  yAxisId="left"
                  domain={leftDomain}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: leftMetric.color }}
                  tickFormatter={(value) => formatYAxisTick(value, leftMetric.type)}
                />

                {/* Right Y Axis */}
                <YAxis
                  yAxisId="right"
                  domain={rightDomain}
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: rightMetric.color }}
                  tickFormatter={(value) => formatYAxisTick(value, rightMetric.type)}
                />
              </>
            )}

            {/* Tooltip */}
            <Tooltip content={<CustomTooltip />} />

            {/* Custom Legend */}
            <Legend
              content={<CustomLegend />}
            />

            {/* Area fills for gradient effect */}
            <Area
              yAxisId="left"
              type="linear"
              dataKey={`${leftMetric.key}Area`}
              stroke="none"
              fill={`url(#${leftMetric.gradientId})`}
              fillOpacity={1}
              legendType="none"
              name=""
            />

            <Area
              yAxisId={useSingleYAxis ? "left" : "right"}
              type="linear"
              dataKey={`${rightMetric.key}Area`}
              stroke="none"
              fill={`url(#${rightMetric.gradientId})`}
              fillOpacity={1}
              legendType="none"
              name=""
            />

            {/* Lines */}
            <Line
              yAxisId="left"
              type="linear"
              dataKey={leftMetric.key}
              stroke={leftMetric.color}
              strokeWidth={3}
              dot={{ fill: leftMetric.color, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: leftMetric.color, strokeWidth: 2 }}
              name={leftMetric.name}
            />

            <Line
              yAxisId={useSingleYAxis ? "left" : "right"}
              type="linear"
              dataKey={rightMetric.key}
              stroke={rightMetric.color}
              strokeWidth={3}
              dot={{ fill: rightMetric.color, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: rightMetric.color, strokeWidth: 2 }}
              name={rightMetric.name}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PPCLineChart;